import React from 'react';
import { Rocket, Smartphone, CreditCard, Users, TrendingUp } from 'lucide-react';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { marketData } from '../../../data/terminalData';

interface DigitalEconomyPanelProps {
  isExpanded?: boolean;
}

export const DigitalEconomyPanel: React.FC<DigitalEconomyPanelProps> = ({ isExpanded = false }) => {
  const { digital } = marketData;

  const unicorns = [
    {
      name: 'Uzum',
      valuation: 1.1,
      sector: 'Fintech & E-commerce',
    },
    {
      name: 'Humans.uz',
      valuation: 1.4,
      sector: 'Super App',
    },
  ];

  const metrics = [
    { label: 'Internet', value: `${digital.internetPen}%`, icon: Smartphone },
    { label: 'Digital Payments', value: '$10.2B/mo', icon: CreditCard },
    { label: 'Fintech Users', value: `${digital.fintechUsers}M`, icon: Users },
  ];

  return (
    <div className="bg-terminal-panel border border-terminal-border rounded-lg overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 border-b border-terminal-border bg-terminal-bg/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Rocket size={18} className="text-data-green" />
          <h2 className="text-base font-bold text-white uppercase tracking-wide">Digital Economy</h2>
        </div>
        <span className="text-xs text-data-amber font-semibold px-2 py-1 bg-data-amber/10 rounded border border-data-amber/20">
          {digital.unicorns} UNICORNS
        </span>
      </div>

      <div className="flex-1 p-5">
        {/* Unicorns Hero */}
        <div className="bg-gradient-to-b from-data-amber/10 to-transparent rounded-lg p-4 border border-data-amber/20 mb-5">
          <div className="text-xs text-data-amber uppercase tracking-wider text-center mb-3">
            Uzbekistan Unicorns
          </div>

          {/* Unicorn Cards */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {unicorns.map((company) => (
              <div
                key={company.name}
                className="bg-terminal-bg/60 rounded-xl p-4 border border-terminal-border/50 text-center"
              >
                <div className="text-sm font-bold text-white mb-1">{company.name}</div>
                <div className="text-3xl font-mono font-black text-data-amber mb-1">
                  ${company.valuation}B
                </div>
                <div className="text-xs text-gray-500">{company.sector}</div>
              </div>
            ))}
          </div>

          {/* Combined Value */}
          <div className="text-center py-2 bg-terminal-border/30 rounded-lg">
            <span className="text-xs text-gray-400">Combined: </span>
            <span className="text-lg font-mono font-bold text-data-amber">${digital.unicornValue}B</span>
          </div>
        </div>

        {/* Digital Metrics */}
        <div className="grid grid-cols-3 gap-3">
          {metrics.map((metric) => (
            <div key={metric.label} className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
              <metric.icon size={16} className="text-gray-500 mx-auto mb-2" />
              <div className="text-xl font-mono font-bold text-white">{metric.value}</div>
              <div className="text-xs text-gray-500 mt-1">{metric.label}</div>
            </div>
          ))}
        </div>

        {/* Growth Badge */}
        <div className="mt-4 flex items-center justify-center gap-2 py-2 bg-data-green/10 rounded-lg border border-data-green/20">
          <TrendingUp size={14} className="text-data-green" />
          <span className="text-xs text-data-green">
            <span className="font-bold">+45%</span> YoY digital payments growth
          </span>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-terminal-border/50 flex items-center justify-between">
        <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
        <span className="text-xs text-gray-500">Dec 2025</span>
      </div>
    </div>
  );
};

export default DigitalEconomyPanel;
